import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquareText, ChevronDown, Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GlassCard } from '@/components/custom/GlassCard';
import { fetchTicketUpdates } from '@/hooks/useAddressValidation';
import type { TicketUpdateItem } from '@uniticket/shared';

// Read-only discussion thread of the Uniticket ticket (comments + system changes), shown ABOVE the
// validation verdict so the reviewer sees the exchanges that already happened before deciding
// (user ask 2026-07-24/27). Best-effort fetch — never blocks the window. Sourced from the Uniticket
// DB: authors AND internal notes included (fuchsia « Interne » badge — AV is internal-only, the same
// audience as Uniticket's internal notes). refreshKey lets the page force a same-ticket refetch
// right after an action posts a new message (Send to Build / escalate / question / verdict).
export function TicketThread({ ticketId, refreshKey = 0 }: { ticketId: string; refreshKey?: number }) {
  const { t } = useTranslation(['tickets']);
  const [updates, setUpdates] = useState<TicketUpdateItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastTicketIdRef = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    // Wipe only when the TICKET changed (a previous ticket's history must never bleed into the next
    // one); a same-ticket refresh (refreshKey bump) keeps the current list shown while the fresh one
    // loads — no flicker.
    if (lastTicketIdRef.current !== ticketId) setUpdates(null);
    lastTicketIdRef.current = ticketId;
    setFailed(false);
    fetchTicketUpdates(ticketId)
      .then((res) => { if (alive) setUpdates(res.data); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [ticketId, refreshKey]);

  // Chronological reading, landing on the LATEST exchange (bottom) once loaded/expanded.
  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [open, updates]);

  // Everything carrying a text is a "message" bubble (public comments AND internal notes — the DB
  // source includes them; AV is internal-only, same audience as Uniticket's internal notes).
  const isMessage = (u: TicketUpdateItem) => u.comment != null && u.comment !== '';
  const comments = updates?.filter(isMessage).length ?? 0;

  return (
    <GlassCard className="p-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-left"
        aria-expanded={open}
      >
        <MessageSquareText className="w-4 h-4 text-brand-rose" />
        <h2 className="text-sm font-bold text-slate-800">
          {t('tickets:validation.thread.title')}
          {updates && <span className="ml-1.5 text-xs font-semibold text-slate-400">({comments})</span>}
        </h2>
        <ChevronDown className={cn('ml-auto w-4 h-4 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="mt-3">
          {updates == null && !failed && (
            <div className="flex items-center gap-2 py-3 text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {failed && <p className="py-2 text-sm text-slate-400">{t('tickets:validation.thread.error')}</p>}
          {updates != null && updates.length === 0 && (
            <p className="py-2 text-sm text-slate-400">{t('tickets:validation.thread.empty')}</p>
          )}
          {updates != null && updates.length > 0 && (
            <div ref={scrollRef} className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {updates.map((u) =>
                isMessage(u) ? (
                  <div
                    key={u.id}
                    className={cn(
                      'rounded-xl border px-3 py-2',
                      u.internal
                        ? 'border-fuchsia-100 bg-fuchsia-50/60'   // internal note — Uniticket's purple look
                        : 'border-slate-100 bg-slate-50/70',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                        {u.authorName ?? '—'}
                        {u.internal && (
                          <span className="inline-flex items-center gap-0.5 rounded bg-fuchsia-100 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-fuchsia-700">
                            <Lock className="w-2.5 h-2.5" /> {t('tickets:validation.thread.internalBadge')}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[10px] text-slate-400">{formatDate(u.createdAt)}</span>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-700">{u.comment}</p>
                  </div>
                ) : (
                  // System line — status/field change, kept compact so the comments stay the focus.
                  <div key={u.id} className="flex items-baseline gap-2 px-1 text-[11px] text-slate-400">
                    <span className="italic">
                      {(u.authorName ? `${u.authorName} — ` : '')
                        + (u.fieldName
                          ? `${u.fieldName}: ${u.oldValue ?? '—'} → ${u.newValue ?? '—'}`
                          : u.action.toLowerCase().replace(/_/g, ' '))}
                    </span>
                    <span className="ml-auto shrink-0 text-[10px]">{formatDate(u.createdAt)}</span>
                  </div>
                ),
              )}
            </div>
          )}
          <p className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
            <Lock className="w-3 h-3" /> {t('tickets:validation.thread.internalHint')}
          </p>
        </div>
      )}
    </GlassCard>
  );
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
